/** 알림 한 줄. 앱 `A-30`(탭)과 웹 `W-60`(벨 드롭다운)이 **같은 함수**로 그린다.
 *
 * ## 출처가 둘이다 (2026-10-31 갱신. DATA_DICTIONARY 43장)
 *
 * - **알림 행(`Notification`, 0004)**: 2026-10-31(`feat/notif-read`)부터 서버가 행을 쓴다.
 *   처리 회차 끝(`COMPLETED` · `FAILED`)과 내보내기(`EXPORTED`)에서 INSERT 하고,
 *   `GET /api/notifications` 가 `NotificationView` 로 읽어 준다. 읽음(`readAt`)도 거기 저장된다.
 *   보존 기간 · 목록 상한은 아래 `NOTIFICATION_RETENTION_DAYS` · `NOTIFICATION_LIST_LIMIT`.
 * - **작업 목록 파생(`notificationLine`)**: 행이 생기기 전의 길이다. 2026-10-06 에 **이 계정의
 *   작업 목록 한 줄(`JobListItemView`)에서 만들도록** 올라왔다. 행 길로 옮기는 동안 화면이 아직
 *   이 길을 쓴다. 어느 갈래를 남길지(진행 중 줄만 남길지, 걷을지)는 design-manager 답을
 *   따른다(43.7). 지어낸 줄은 없다. 이 변환이 앱(`(tabs)/notifications.tsx` 의 `toNote`) 안에만
 * 있던 동안 웹 벨은 예시 알림을 그리다가 걷혔다(#179). 목업 `W-60` 머리말이 앱과 웹의 목록을
 * 한 묶음으로 묶어 두었으므로 **변환은 한 벌**이어야 한다. 화면이 다른 것은 보조줄 꼬리와
 * 누르면 가는 주소뿐이다.
 *
 * ## 화면별 차이 (목업이 그어 둔 것)
 *
 * | 줄 | 앱 `A-30` | 웹 `W-60` |
 * |---|---|---|
 * | 준비됨 | `상품명 · 7장 완료, 1장 실패` | 같은 글 뒤에 ` · {앱/웹}에서 올린 작업` |
 * | 내보내기 완료 | `여기서 바로 받기` | 보조줄 없음 |
 *
 * ⛔ **목업에 있지만 지금 데이터로 사실이 아닌 글자는 넣지 않는다** (2026-10-06 design-manager).
 * - 진행 중 `앱을 닫아도 계속됩니다`: 서버가 도는 동안만 참인데, 목록 한 줄로는 미룬 장만
 *   남아 아무것도 돌지 않는 판(웹 `Making.tsx` 의 `RESUMABLE`)을 가를 수 없다.
 * - 내보내기 `파일 9개` · `웹에서 내보냄`: 목록에 파일 수도, 마지막으로 내보낸 곳도 없다.
 *   `imageDoneCount` 는 사진 장수라 파일 수가 아니다. 다시 받기는 이력을 쌓지 않아
 *   `lastActivity` 로도 내보낸 곳을 알 수 없다. 살리려면 목록에 그 칸이 먼저 있어야 한다.
 * - 장 단위 실패 줄(`4번 사진 · 경계를 못 찾음 · …`): 목록에 장별 사유가 없다.
 * - 안 읽음 점 · `모두 읽음`: **작업 목록 파생 줄에는** 읽음을 붙일 자리가 없다. 행 길
 *   (`NotificationView.readAt`)에서만 살아난다.
 *
 * 위 넷 가운데 `웹에서 내보냄` 과 장 단위 실패 줄도 행 길에서 살아난다. 내보낸 곳은
 * `NotificationView.exportSource`(조인 칸), 장 단위 실패는 행의 `body` 에 박힌 글자다.
 * 위 ⛔ 는 **목록 파생 갈래**에 대한 것이다.
 *
 * ⛔ 런타임 코드는 순수 TypeScript 만 둔다(`index.ts` 머리말). 시각(`now`)도 인자로 받는다.
 */

import type { ActivitySource, JobActivityView, JobListItemView, JobOrigin, NotificationView } from "./domain";
import { formatCount, formatListTime, parseServerDate } from "./format";
import { jobStatusLabel } from "./job-labels";

/* ── 출처 낱말 ─────────────────────────────────────────────────────────
 * 앱 `lib/activity.ts` 에서 올라왔다. 알림 줄(`DISCARDED` 보조줄 · 웹 준비됨 꼬리)이 쓰므로
 * 함께 올려야 앱과 웹이 같은 낱말을 쓴다.
 *
 * 낱말은 `docs/PRD.md` `7.5.9` 표를 따른다. `앱` · `웹` · `서버` 셋뿐이고 `클라이언트` 같은
 * 구현 낱말은 화면에 나가지 않는다. */

const SOURCE_WORD: Record<ActivitySource, string> = {
  APP: "앱",
  WEB: "웹",
  SERVER: "서버",
};

/** 작업을 처음 만든 곳. `UNKNOWN` 은 0002 이전 행 전용이라 셀러에게 이름을 지어 주지 않고
 *  `null` 로 돌려준다. **모르는 것을 `앱` 이라고 적으면 그 줄이 거짓말을 한다.** */
export function originWord(origin: JobOrigin): string | null {
  return origin === "UNKNOWN" ? null : SOURCE_WORD[origin];
}

export function activityWord(source: ActivitySource): string {
  return SOURCE_WORD[source];
}

/** `웹 · 14:20 상품명 고침` 한 줄. **구분점은 출처 뒤에 하나뿐이다.** 목업의 보조줄이
 *  시각과 한 일을 공백으로 잇는다(`.jitem .b`).
 *
 * **빈 조각은 빼고 남은 것으로 읽히게 한다.** `formatListTime` 은 시각을 못 읽으면 빈
 * 문자열을 돌려준다(그것이 기호가 아니라 **없음**이다). 그 자리를 기호로 채우면 없는 것을
 * 있는 것처럼 보이게 한다. */
export function activityLine(
  activity: JobActivityView | null,
  fallback: string,
  now: Date = new Date(),
): string {
  if (!activity) return fallback;
  const when = formatListTime(activity.at, now);
  const what = when ? `${when} ${activity.summary}` : activity.summary;
  return `${activityWord(activity.source)} · ${what}`;
}

/* ── 알림 줄 ─────────────────────────────────────────────────────────── */

/** 어느 화면이 그리는가. 보조줄 꼬리만 갈린다(머리말 표). */
export type NotificationSurface = "app" | "web";

/** 아이콘 원의 색 갈래. 목업 `.ni.ok` · `.fail` · `.run` · `.idle` 과 같은 이름이다. */
export type NotificationTone = "ok" | "fail" | "run" | "idle";

/** 누르면 가는 곳. **주소가 아니라 종류다.** 앱과 웹은 주소 체계가 달라 각자 옮긴다.
 *  웹에는 `returned`(나갔다 돌아온 셀러용 화면)가 없어 `result` 로 간다. */
export type NotificationTarget = "result" | "returned" | "progress" | "export" | "history";

/** 화면이 그리는 줄 하나. ⚠️ `NotificationView`(서버 `Notification` 행의 DTO)와 다르다.
 *
 * 2026-10-31 부터 서버가 행을 쓴다. **정리 방향(43.7)**: 행 DTO → 이 화면 줄로 바꾸는 함수를
 * 이 파일에 하나 두어 앱 · 웹이 같은 함수를 쓰고, 작업 목록 파생 갈래(`notificationLine`)는
 * design-manager 답에 따라 진행 중 줄만 남기거나 걷는다. 화면 줄의 모양(tone · glyph · target ·
 * 꼬리)은 출처가 행이든 목록이든 같아야 하므로 **이 타입은 남긴다.**
 *
 * ⚠️ `NotificationView.jobId` 는 `null` 일 수 있다(작업과 무관한 알림). 그런 종류가 생기면 이
 * 칸을 `string | null` 로 넓히는 판단이 함께 온다. 지금 쓰는 `COMPLETED` · `FAILED` ·
 * `EXPORTED` 는 모두 작업에 붙는다. */
export interface NotificationLine {
  jobId: string;
  tone: NotificationTone;
  glyph: string;
  title: string;
  /** 보조줄. **빈 문자열이면 그 줄을 그리지 않는다**(웹 내보내기 완료). */
  body: string;
  /** 웹 준비됨의 ` · {앱}에서 올린 작업` 꼬리. 웹은 출처를 배지로 그려서 글과 떼어 둔다.
   *  앱이거나 출처를 모르면(`UNKNOWN`) `null`. */
  origin: { word: string; suffix: string } | null;
  /** 목록 시각(`formatListTime`). 못 읽으면 `""`. */
  at: string;
  target: NotificationTarget;
}

/** 알림 행의 보존 일수. **쓰는 쪽이 행에 박는다**(`expiresAt`). 기간이 바뀌어도 이미 쌓인
 *  행은 흔들리지 않는다(0004 머리말). 만료된 행은 **지우지 않고 읽기에서 거른다**(크론 없음, 43.7).
 *
 * ⛔ **`expiresAt` 을 JS 의 ISO 문자열로 박지 않는다.** 이 표의 시각은 D1 `CURRENT_TIMESTAMP`
 * 꼴(`YYYY-MM-DD HH:MM:SS`)이고 읽기는 `"expiresAt" > CURRENT_TIMESTAMP` 로 **문자열 비교**를
 * 한다. ISO(`…T…Z`)를 섞으면 `T`(0x54)가 공백(0x20)보다 커서 만료일 당일 행이 하루 동안 살아
 * 있는 것으로 읽힌다. SQL 안에서 `datetime(CURRENT_TIMESTAMP, ?)` 에
 * `NOTIFICATION_EXPIRES_MODIFIER` 를 묶어 만든다. `readAt` 도 `CURRENT_TIMESTAMP` 로 박는다. */
export const NOTIFICATION_RETENTION_DAYS = 30;

/** `datetime(CURRENT_TIMESTAMP, ?)` 에 묶는 SQLite 시각 수식어(`+30 days`). */
export const NOTIFICATION_EXPIRES_MODIFIER = `+${NOTIFICATION_RETENTION_DAYS} days`;

/** `GET /api/notifications` 가 내려 주는 줄 수 상한. 페이지 넘김은 없다.
 *  안 읽음 수(`unreadCount`)는 이 상한과 무관하게 만료되지 않은 행 전부를 센다. */
export const NOTIFICATION_LIST_LIMIT = 50;

/** 이보다 오래 전에 끝난 작업은 "나가 있는 동안 끝난 것"으로 본다.
 * TODO(prd-manager): 이탈 판정 기준은 미정이다. 지금은 5분으로 둔다(앱에서 그대로 옮겼다). */
export const NOTIFICATION_AWAY_AFTER_MS = 5 * 60_000;

/** 알림 목록이 훑는 작업 수. 앱은 홈과 같은 질문(`HOME_PAGE_SIZE`)을 쓰고 웹은 이 값을 쓴다.
 *  두 값이 같아야 양쪽 목록이 같다(`scripts/notifications-shared.test.mjs` 가 맞댄다). */
export const NOTIFICATION_JOB_LIMIT = 30;

/** 상품명이 없을 때 부르는 말. 목록·홈과 같은 말이라야 셀러가 같은 작업으로 읽는다.
 *  ⚠️ `새 상품` 으로 바꾸지 않는다. 내보낸 판까지 그렇게 부르는 것을 용어 표가 막는다(O-67). */
const UNNAMED = "이름 없는 작업";

/** 목록 한 줄을 알림 한 줄로 바꾼다. **줄 구조는 목업 `A-30` · `W-60` 을 옮긴 것이다.**
 *
 * 규칙은 둘이고 목업이 줄마다 갈라 뒀다.
 * - **제목이 무슨 일인지 말하고 보조줄이 대상을 말하는 줄**: `상세페이지가 준비됐어요` /
 *   `무광 세라믹 머그 · 7장 완료, 1장 실패`. 실패 줄도 같은 꼴이다.
 * - **일 전체가 그 상품 하나인 줄**: `… 만드는 중` · `… 내보내기 완료`. 상품명이 **제목**에 든다.
 *
 * ⚠️ **`READY` 와 `EXPORTED` 를 한 갈래로 묶지 않는다.** 묶여 있던 동안 내보내기까지 끝낸
 * 작업이 `상세페이지가 준비됐어요` 로 떴다. 셀러가 이미 한 일을 다시 하라고 부르는 줄이다.
 *
 * ⚠️ **`DISCARDED` 를 마지막 갈래가 받지 않는다.** 결제 전에 그만둬 원본을 파기한 작업이
 * `아직 시작하지 않았습니다` 로 떠서, 누르면 사진이 없는 화면으로 갔다.
 *
 * `READY` 는 처리가 끝난 상태라 **못 만든 장 = 전체 − 만든 장**이 정확하다. 진행 중인 작업의
 * `전체 − 만든 장` 은 아직 안 온 장까지 포함하므로 그 갈래에서는 쓰지 않는다. */
export function notificationLine(
  job: JobListItemView,
  surface: NotificationSurface,
  now: Date = new Date(),
): NotificationLine {
  const lastAt = job.lastActivity?.at ?? job.createdAt;
  const at = formatListTime(lastAt, now);
  const name = job.productName ?? UNNAMED;
  const done = job.imageDoneCount;
  const total = job.imageTotalCount;
  const base = { jobId: job.id, at, origin: null };

  if (job.status === "READY") {
    const finished = parseServerDate(lastAt)?.getTime() ?? 0;
    const away = now.getTime() - finished > NOTIFICATION_AWAY_AFTER_MS;
    const failed = Math.max(0, total - done);
    const word = surface === "web" ? originWord(job.origin) : null;
    return {
      ...base,
      tone: "ok",
      glyph: "✓",
      title: "상세페이지가 준비됐어요",
      body: failed > 0 ? `${name} · ${done}장 완료, ${failed}장 실패` : `${name} · ${done}장 완료`,
      origin: word ? { word, suffix: "에서 올린 작업" } : null,
      // 그 자리에 계속 있던 셀러와 나갔다 돌아온 셀러는 다른 화면이 필요하다.
      target: away ? "returned" : "result",
    };
  }
  if (job.status === "EXPORTED") {
    return {
      ...base,
      tone: "idle",
      glyph: "↓",
      title: `${name} 내보내기 완료`,
      // 앱은 `A-52` 에서 바로 받는다. 웹은 받을 곳을 말하지 않는다(머리말: 파일 수·내보낸 곳이
      // 목록에 없다).
      body: surface === "app" ? "여기서 바로 받기" : "",
      target: "export",
    };
  }
  if (job.status === "FAILED") {
    return {
      ...base,
      tone: "fail",
      glyph: "!",
      title: "한 장도 만들지 못했어요",
      body: `${name} · ${total}장 모두 배경을 못 지웠습니다`,
      target: "progress",
    };
  }
  if (job.status === "PROCESSING") {
    return {
      ...base,
      tone: "run",
      glyph: "↑",
      title: `${name} 만드는 중`,
      body: formatCount(done, total),
      target: "progress",
    };
  }
  if (job.status === "DISCARDED") {
    return {
      ...base,
      tone: "idle",
      glyph: "↓",
      title: `${name} ${jobStatusLabel(job.status).label}`,
      // 마지막으로 무엇을 했는지가 이 줄이 말할 수 있는 전부다. 홈의 보조줄과 같은 함수다.
      body: activityLine(job.lastActivity, `${total}장`, now),
      // **결과 화면으로 보내지 않는다.** 원본이 없어 볼 것이 없다.
      target: "history",
    };
  }
  // TODO(design-manager): `PAID`(결제됐고 시작 전)도 여기로 온다. 목업에 그 줄이 없어
  // 지금은 `초안` 과 같은 말을 쓰는데, 결제가 붙으면 다른 말이 필요하다.
  return {
    ...base,
    tone: "idle",
    glyph: "↓",
    title: "사진만 올려둔 작업이 있어요",
    body: `${name} · ${total}장 · 아직 시작하지 않았습니다`,
    target: "progress",
  };
}

/** 목록 전체. 순서는 서버 목록 순서 그대로다(줄을 다시 세우지 않는다). */
export function notificationLines(
  jobs: readonly JobListItemView[],
  surface: NotificationSurface,
  now: Date = new Date(),
): NotificationLine[] {
  return jobs.map((job) => notificationLine(job, surface, now));
}

/** 줄 하나를 읽는 글(스크린리더 라벨). 웹의 출처 배지도 글로 편다. */
export function notificationText(line: NotificationLine): string {
  const tail = line.origin ? ` · ${line.origin.word}${line.origin.suffix}` : "";
  return [line.title, `${line.body}${tail}`].filter(Boolean).join(" ");
}

/* ── 행 길 (2026-10-31, `feat/notif-read`. PRD `7.6.12` 「저장 알림 회차의 규격」) ─────────
 *
 * 화면이 그리는 목록은 **저장 행 + 지금 만드는 중인 작업의 줄**이다(ⓓ). 초안 · 파기됨 줄은 걷었다
 * (알림 종류 표에 없다). 위 `notificationLine` 은 만드는 중 갈래로만 쓴다.
 *
 * ⭐ **꼬리는 박지 않고 여기서 붙인다**(ⓒ). 서버가 읽는 순간 판정해 내린 구조 칸(`remakeFreeAvailable`
 * · `receivable` · `jobOrigin` · `exportSource`)을 보고 화면마다 다른 꼬리를 단다. */


/** 화면 줄 + 읽음. `notificationId` 가 `null` 이면 만드는 중 줄이다(행이 아니라 읽음이 없다). */
export interface NotificationFeedLine extends NotificationLine {
  /** 목록 키. 행이면 행 id, 만드는 중 줄이면 `run:{jobId}`. */
  key: string;
  notificationId: string | null;
  /** 안 읽음 점. 만드는 중 줄은 언제나 `false`(ⓔ). */
  unread: boolean;
  /** 섞어 세울 때 쓰는 시각(ms). 못 읽으면 `0`. */
  sortAt: number;
}

/** 장 단위 실패의 값 꼬리. 서버가 `remakeFreeAvailable` 로 참이라고 말한 때만 붙는다(ⓒ). */
const REMAKE_FREE_TAIL = "다시 만들어도 더 내실 금액 없음";
/** 앱 내보내기 완료의 꼬리. 받기가 열려 있을 때만(ⓒ). */
const RECEIVE_HERE_TAIL = "여기서 바로 받기";

const TONE_OF: Record<NotificationView["type"], { tone: NotificationTone; glyph: string }> = {
  COMPLETED: { tone: "ok", glyph: "✓" },
  FAILED: { tone: "fail", glyph: "!" },
  EXPORTED: { tone: "idle", glyph: "↓" },
  // 돌아간 돈은 실패가 아니라 정상적으로 끝난 일이다. 색은 `ok`, 글리프만 `₩`(PRD 7.6.12).
  REFUNDED: { tone: "ok", glyph: "₩" },
  PROGRESS: { tone: "run", glyph: "↑" },
};

function targetOf(view: NotificationView, now: Date): NotificationTarget {
  // 그새 파기된 작업은 결과 화면으로 보내지 않는다. 볼 원본이 없다.
  if (view.jobStatus === "DISCARDED") return "history";
  switch (view.type) {
    case "COMPLETED": {
      const at = parseServerDate(view.createdAt)?.getTime() ?? 0;
      return now.getTime() - at > NOTIFICATION_AWAY_AFTER_MS ? "returned" : "result";
    }
    case "FAILED":
      // 전량 실패는 `A-23`(작업 첫 주소)로, 장 단위 실패는 못 만든 장의 빈 자리가 있는 결과 화면으로.
      return view.jobStatus === "FAILED" ? "progress" : "result";
    case "EXPORTED":
      return "export";
    case "PROGRESS":
      return "progress";
    case "REFUNDED":
      return "result";
  }
}

/** 저장 행 하나 → 화면 줄. 작업에 붙지 않은 행(`jobId === null`)은 지금 갈 곳이 없어 `null`
 *  (지금 쓰는 세 종류는 모두 작업에 붙는다. 그런 종류가 생기면 `NotificationLine.jobId` 를 넓힌다). */
export function storedNotificationLine(
  view: NotificationView,
  surface: NotificationSurface,
  now: Date = new Date(),
): NotificationFeedLine | null {
  if (view.jobId === null) return null;
  const { tone, glyph } = TONE_OF[view.type];
  let body = view.body;
  let origin: NotificationLine["origin"] = null;
  if (view.type === "FAILED" && view.remakeFreeAvailable) body = `${body} · ${REMAKE_FREE_TAIL}`;
  if (view.type === "EXPORTED") {
    if (surface === "app" && view.receivable) body = `${body} · ${RECEIVE_HERE_TAIL}`;
    // 웹만 내보낸 곳을 배지로 단다. 서버가 한 일(`SERVER`)이나 모르면 붙이지 않는다.
    if (surface === "web" && (view.exportSource === "APP" || view.exportSource === "WEB")) {
      origin = { word: activityWord(view.exportSource), suffix: "에서 내보냄" };
    }
  }
  if (view.type === "COMPLETED" && surface === "web" && view.jobOrigin) {
    const word = originWord(view.jobOrigin);
    if (word) origin = { word, suffix: "에서 올린 작업" };
  }
  return {
    key: view.id,
    notificationId: view.id,
    unread: view.readAt === null,
    sortAt: parseServerDate(view.createdAt)?.getTime() ?? 0,
    jobId: view.jobId,
    tone,
    glyph,
    title: view.title,
    body,
    origin,
    at: formatListTime(view.createdAt, now),
    target: targetOf(view, now),
  };
}

/** 지금 만드는 중인 작업의 줄(ⓓ). 행이 아니다. 시각은 그 작업의 마지막 활동. */
export function progressNotificationLines(
  jobs: readonly JobListItemView[],
  now: Date = new Date(),
): NotificationFeedLine[] {
  return jobs
    .filter((job) => job.status === "PROCESSING")
    .map((job) => ({
      ...notificationLine(job, "app", now),
      key: `run:${job.id}`,
      notificationId: null,
      unread: false,
      sortAt: parseServerDate(job.lastActivity?.at ?? job.createdAt)?.getTime() ?? 0,
    }));
}

/** 화면이 그리는 목록 전체. **시각 순**(최신 위)이고, 같은 시각의 저장 행은 서버 순서를 지킨다
 *  (서버가 `COMPLETED` 를 `FAILED` 위에 둔다. ⓐ). `Array.prototype.sort` 는 안정 정렬이다. */
export function notificationFeed(
  stored: readonly NotificationView[],
  jobs: readonly JobListItemView[],
  surface: NotificationSurface,
  now: Date = new Date(),
): NotificationFeedLine[] {
  const rows = stored
    .map((view) => storedNotificationLine(view, surface, now))
    .filter((line): line is NotificationFeedLine => line !== null);
  return [...rows, ...progressNotificationLines(jobs, now)].sort((a, b) => b.sortAt - a.sortAt);
}
